import React from 'react';
import { FormSpy, AnyObject } from 'react-final-form';
import { FormApi, FieldValidator } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { EnquiryDefaultConfig } from '../../EnquirySummaryRepository';
import { AreaOfLawSelector, IAreaOfLawAutoCompleteItem } from '../../selectors/AreaOfLawSelector';
import { TranslationTextList } from '../../../locales/common';
import { SpeechTextField } from '../../../components/SpeechTextField';
import EventNoteIcon from '@material-ui/icons/EventNote';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('EnquiryDetailsPanel');

// Explicit request (EnquiryFormDrawer split): the "Enquiry Details" accordion (nature of enquiry, area of law).
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface EnquiryDetailsPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    isExpanded: boolean;
    isKeyOpportunity: boolean;
    enquiryDefaultConfig: EnquiryDefaultConfig | undefined;
    translate: (key: keyof TranslationTextList) => string;
    requiredValidator: FieldValidator<any>;
    onSelectionChange: (form: FormApi<Enquiry>, selection: IAreaOfLawAutoCompleteItem, name: string, values: AnyObject) => void;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    isActivityTab: boolean;
}

interface EnquiryDetailsPanelBodyProps extends EnquiryDetailsPanelProps {
    formValues: AnyObject;
}

const EnquiryDetailsPanelBody: React.FC<EnquiryDetailsPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, isExpanded, isKeyOpportunity, enquiryDefaultConfig, translate, requiredValidator, onSelectionChange, onExpandChange, isActivityTab,
        formValues,
    } = bodyProps;
    const values = formValues;

    let panelTitle = [values.areaOfLaw, values.natureOfEnquiry].filter(n => n).join(', ');

    return (
        <Accordion
            className={classes.expansionPanel}
            expanded={isExpanded}
            onChange={onExpandChange}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="enquiry-details-content"
                id="enquiry-details-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipOrange)}>
                        <EventNoteIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelOrange, classes.sectionLabelText)}>
                        {`${isKeyOpportunity
                            ? translate('KeyOpportunity')
                            : translate('Enquiry')}
                            Details`}
                    </Typography>
                </div>
                <div className={classes.infoContainer}>
                    <div className="col">
                        <div className="value">
                            {panelTitle}
                        </div>
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid
                        item={true}
                        xs={12}
                        className={clsx(classes.customFieldGrid, classes.fieldRowGap)}
                    >
                        <Field
                            name="natureOfEnquiry"
                            // validate={required}
                            subscription={{ touched: true, error: true, value: true }}
                            validate={enquiryDefaultConfig?.isNatureOfEnquiryMandatory ? requiredValidator : undefined}
                        >
                            {({ input, meta }) => (
                                <SpeechTextField
                                    input={input}
                                    label={translate('Nature Of Enquiry')}
                                    multiline
                                    variant="outlined"
                                    size="small"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    helperText={meta.error && meta.touched ? `${translate('Nature Of Enquiry')} is required` : ''}
                                    required={enquiryDefaultConfig?.isNatureOfEnquiryMandatory}
                                />
                                /*
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label={t('Nature Of Enquiry')}
                                    type="text"
                                    multiline={true}
                                    // required={true}
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? `${t('Nature Of Enquiry')} is required`
                                            : ''
                                    }
                                    required={props.enquiryDefaultConfig?.isNatureOfEnquiryMandatory}
                                />
                                */
                            )}
                        </Field>
                    </Grid>
                    <Grid
                        item={true}
                        xs={12}
                        className={clsx(classes.customFieldGrid, classes.fieldRowGap)}
                    >
                        <Field
                            name="areaOfLaw"
                            // tslint:disable-next-line: max-line-length
                            validate={!isActivityTab ? requiredValidator : undefined}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div>
                                    <AreaOfLawSelector
                                        variant="outlined"
                                        size="small"
                                        {...input}
                                        label="Area of Law"
                                        name="areaOfLaw"
                                        required={!isActivityTab}
                                        className={classes.textField}
                                        error={meta.error && meta.touched}
                                        // tslint:disable-next-line: max-line-length
                                        value={{ label: values.areaOfLaw, value: values.practiceAreaGuidID }}
                                        // tslint:disable-next-line: max-line-length
                                        onSelection={(selection: IAreaOfLawAutoCompleteItem, name: string) => {
                                            const values = form.getState().values;
                                            return onSelectionChange(form, selection, name, values);
                                        }}
                                        onBlur={input.onBlur}
                                        helperText={
                                            meta.error &&
                                                meta.touched
                                                ? 'Area of law is required'
                                                : undefined
                                        }
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
EnquiryDetailsPanelBody.displayName = 'EnquiryDetailsPanelBody';

export const EnquiryDetailsPanel: React.FC<EnquiryDetailsPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <EnquiryDetailsPanelBody
                {...panelProps}
                formValues={{
                    areaOfLaw: values.areaOfLaw,
                    natureOfEnquiry: values.natureOfEnquiry,
                    practiceAreaGuidID: values.practiceAreaGuidID,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
EnquiryDetailsPanel.displayName = 'EnquiryDetailsPanel';
